import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, Plus, Minus, ArrowRight } from 'lucide-react';

const FAQS = [
  {
    q: 'Do you need to cut my fenders for a widebody install?',
    a: 'Yes — widebody installations require fender cutting for proper wheel and tire clearance.',
  },
  {
    q: 'Can I bring my own kit or parts?',
    a: 'Yes. We can install parts from other brands including Fortune and those from partner brands such as DarwinPro, Paktechz, Sooqoo, and others.',
  },
  {
    q: 'Do you paint or finish the parts?',
    a: 'Painting is done externally, but we prepare parts for paint and can coordinate with your preferred shop.',
  },
  {
    q: 'Can you correct poor fitment from another brand?',
    a: 'Yes — we can adjust, reshape, or refine components for better alignment.',
  },
  {
    q: 'How is shipping handled on my order?',
    a: 'Shipping is billed separately from your product. After you place and pay for the part, we contact you to confirm the production timeline and ship a separate invoice with the actual carrier, tracking, and final shipping cost once your order is ready to ship.',
  },
  {
    q: 'When does production start, and how long until my part is ready?',
    a: 'Once you place an order, our team reaches out within 1 business day to confirm your spec and finalize the production timeline. Fulfillment timelines vary by product — please contact us for specific lead times and availability.',
  },
  {
    q: 'What is the difference between "In Stock" and "Special Order"?',
    a: 'Most of our parts are built to order — these are labeled Special Order and are configured to your vehicle. When a part is already finished and ready to ship from our shop, it is labeled In Stock. Both options check out the same way; the only difference is lead time.',
  },
];

const Faq = () => {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div className="bg-black text-white min-h-screen">
      {/* Hero */}
      <section className="pt-40 pb-12 border-b border-white/10">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-5">
            <span className="h-12 w-12 rounded-full border-2 border-[#FF6B00] flex items-center justify-center">
              <HelpCircle className="text-[#FF6B00]" size={22} />
            </span>
            <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold">
              SUPPORT
            </div>
          </div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tight">
            FREQUENTLY ASKED QUESTIONS
          </h1>
          <div className="mt-5 h-[2px] w-full bg-gradient-to-r from-[#FF6B00] via-[#FF6B00]/40 to-transparent" />
          <p className="text-neutral-400 mt-5 max-w-2xl">
            Everything you need to know about ordering, installation, materials, lead times, and shipping. Don&rsquo;t see your question? Reach out to our team.
          </p>
        </div>
      </section>

      {/* FAQ list */}
      <section className="py-16">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <ul className="divide-y divide-white/10 border-t border-b border-white/10">
            {FAQS.map((item, idx) => {
              const isOpen = openIdx === idx;
              return (
                <li key={idx}>
                  <button
                    type="button"
                    onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                    data-testid={`faq-toggle-${idx}`}
                    aria-expanded={isOpen}
                    className="w-full flex items-center justify-between text-left gap-6 py-6 group"
                  >
                    <span
                      className={`font-display text-lg md:text-xl tracking-[0.06em] transition-colors ${
                        isOpen ? 'text-[#FF6B00]' : 'text-white group-hover:text-[#FF6B00]'
                      }`}
                    >
                      {item.q.toUpperCase()}
                    </span>
                    <span
                      className={`shrink-0 h-9 w-9 flex items-center justify-center border transition-colors ${
                        isOpen
                          ? 'border-[#FF6B00] bg-[#FF6B00] text-white'
                          : 'border-white/20 text-white group-hover:border-[#FF6B00] group-hover:text-[#FF6B00]'
                      }`}
                    >
                      {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                    </span>
                  </button>
                  {isOpen && (
                    <div
                      data-testid={`faq-answer-${idx}`}
                      className="pb-6 pr-12 text-[15px] text-neutral-300 leading-relaxed"
                    >
                      {item.a}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* CTA */}
          <div className="mt-14 border border-white/10 bg-[#0a0a0a] p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-2">
                STILL HAVE QUESTIONS?
              </div>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight">
                TALK TO OUR TEAM &mdash; WE&rsquo;LL HELP YOU SPEC IT OUT.
              </h2>
            </div>
            <Link
              to="/contact"
              data-testid="faq-contact-cta"
              className="inline-flex items-center gap-3 bg-[#FF6B00] hover:bg-[#E25500] text-white px-7 py-4 font-display tracking-[0.22em] text-sm font-bold transition-colors"
            >
              GET A QUOTE <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Faq;
